<template>
  <div class="availability-section" :class="{ desktop: $isDesktop }">
    <pu-accordion
      ref="availabilityAccordion"
      :selected="shouldShowAvailabilityDetails"
      :accordion-id="`accordion-pitchtype-availability-${pitchtypeId}`"
      @accordion-open="accordionOpen($event)"
      @accordion-close="$emit('availability-details-changed', false)"
    >
      <template #title>
        <translate tag="h4"> Availability </translate>
      </template>
      <template #opposite-body>
        <pitchtype-mini-availability-calendar-with-availability
          :pitchtype-id="pitchtypeId.toString()"
          :campsite-slug="campsiteSlug"
          :stay-dates="dates"
          :calendar-start-date="miniCalStartDate"
          :should-fetch="true"
          class="availability-calendar-preview"
        />
      </template>

      <template #body>
        <pitchtype-datepicker-availability
          ref="mainCalendar"
          :pitchtype-id="pitchtypeId.toString()"
          :campsite-slug="campsiteSlug"
          :campsite-categories="campsiteCategories"
          :campsite-opening-dates-range="campsiteOpeningDatesRange"
          :stay-dates="dates"
          :party="party"
          :calendar-start-date="calendarStartDate"
          :min-night-stay-applies="minNightStayApplies"
          :selected-dates-price="selectedDatesPrice"
          :show-asterix="showAsterix"
          @datesChanged="$emit('datesChanged', $event)"
          @clearDates="$emit('clearDates')"
          @changeRange="onChangeRange"
          @monthChanged="onMonthChanged"
          @yearChanged="onYearChanged"
        />
        <div v-if="availableToSearch" class="key">
          <span class="cell" />
          <translate>Arrive or depart on this date</translate>
        </div>
      </template>
    </pu-accordion>
  </div>
</template>

<script lang="ts">
import { IsoDateFormatter } from '~/utility/date/IsoDateFormatter'
import type { SimpleMoney } from '~/utility/money'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { scrollIntoView } from '~/utility/scroll'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import type { Party } from '../../components/types'
import { OpeningDatesRange } from '../../types'

@Component({
  emits: [
    'availability-details-changed',
    'datesChanged',
    'rangeChanged',
    'clearDates',
  ],
})
class PitchtypeDetailsAvailabilitySection extends Vue {
  @Prop({ required: true })
  pitchtypeId: number

  @Prop({ required: true })
  campsiteCategories: CampsiteCategory[]

  @Prop({ required: true })
  dates: { start: string; end: string }

  @Prop({ required: false })
  party: Party

  @Prop({ required: true })
  campsiteSlug: string

  @Prop({ required: true, default: false })
  shouldShowAvailabilityDetails: boolean

  @Prop({ default: false })
  minNightStayApplies: boolean

  @Prop()
  campsiteOpeningDatesRange: OpeningDatesRange[]

  @Prop()
  calendarStartDate: string

  @Prop()
  selectedDatesPrice: SimpleMoney

  @Prop()
  availableToSearch: boolean

  @Prop({ default: false })
  showAsterix: boolean

  currentMonth: number | null = null
  currentYear: number | null = null

  get miniCalStartDate() {
    if (this.currentMonth !== null) {
      const baseDate = this.calendarStartDate
        ? new Date(this.calendarStartDate)
        : new Date()
      const year = this.currentYear ?? baseDate.getUTCFullYear()
      const date = new Date(Date.UTC(year, this.currentMonth - 1, 1))
      const today = new Date()
      return date > today
        ? IsoDateFormatter.toIsoDate(date)
        : IsoDateFormatter.toIsoDate(today)
    } else {
      return this.calendarStartDate
    }
  }

  async accordionOpen($event: Event) {
    this.$emit('availability-details-changed', true)
    if ($event.type === 'click') {
      await this.$nextTick(function () {
        scrollIntoView('.availability-section', { block: 'start' })
      })
    }
  }

  onMonthChanged(month: number) {
    this.currentMonth = month
  }

  onYearChanged(year: number) {
    this.currentYear = year
  }

  onChangeRange(dates: Date[]) {
    if (dates.length > 1) {
      this.$emit('rangeChanged', {
        arrive: IsoDateFormatter.toIsoDate(dates[0]),
        depart: IsoDateFormatter.toIsoDate(dates[1]),
      })
    } else {
      this.$emit('rangeChanged', {
        arrive: IsoDateFormatter.toIsoDate(dates[0]),
        depart: null,
      })
    }
  }
}

export default toNative(PitchtypeDetailsAvailabilitySection)
</script>

<style scoped>
.availability-section {
  min-height: 2em;
  margin-top: 0.4em;
}

.availability-section {
  margin: 0 -1em 0 -1em;
  padding: 0 1em;
  background: #eef2f7;
}

.availability-section :deep(.accordion),
.availability-section :deep(.accordion-content) {
  border-top: none;
  border-bottom: none;
  padding: 0;
}

.availability-section :deep(.accordion-content) {
  padding: 0.5em 0;
}

.availability-section :deep(.accordion-title h4) {
  margin: 0;
}

.availability-section :deep(.calendar) {
  cursor: pointer;
  gap: 1em;
}

.availability-calendar-preview {
  min-height: 4em;
}

.availability-calendar-preview :deep(.day) {
  width: 10px;
  height: 10px;
  margin-bottom: 1.5px;
}

.availability-calendar-preview :deep(.week) {
  gap: 1.5px;
}

.availability-calendar-preview :deep(.title) {
  color: var(--colour-dark-blue);
  font-size: var(--small);
  margin-bottom: 6px;
}

.key {
  font-size: var(--small);
  color: var(--text-default);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0.4em 0 0;
}

.cell {
  display: inline-flex;
  width: 1.3em;
  height: 1.3em;
  border-radius: 0.3em;
  margin-right: 0.4em;
  background: var(--colour-light-green);
}

/* desktop & tablet */
.availability-section.desktop {
  margin: 0;
  padding: 0 1.4em;
  border-radius: 0.6em 0.6em 0 0;
}
</style>
